// mountView: draws a tool's answer with its view, in the office app or in an MCP Apps page (Drew's
// bridge). The answer is checked against the view's props first: a tool that drifted from its view
// draws "can't show this here" with a link into Flowology, never a half-drawn card.
import { createRoot } from 'react-dom/client';
import type { ReactElement } from 'react';
import { blueprint, BLUEPRINT_DEFAULT_BRAND, generateTheme, toCss } from '@flowology/tokens';
import { TOOL_VIEWS, type ToolName } from './schemas.ts';
import type { Host } from './host.ts';
import { CallerMatches } from './components/CallerMatches.tsx';
import { CustomerGlance } from './components/CustomerGlance.tsx';
import { Aging } from './components/Aging/Aging.tsx';
import { AgentActivity, ApprovalList } from './components/InboxViews.tsx';
import { PaceView } from './rows/pace-card.tsx';
import { PricebookArt } from './components/PricebookArt.tsx';
import baseCss from './base.css?inline';

const VIEWS = {
  CallerMatches,
  CustomerGlance,
  ApprovalList,
  PaceView,
  Aging,
  AgentActivity,
  PricebookArt,
} as const;

/** The view for a tool's answer, or null when the tool has none or its answer doesn't fit it. */
export function viewFor(tool: string, result: unknown, host: Host): ReactElement | null {
  const v = (TOOL_VIEWS as Record<string, (typeof TOOL_VIEWS)[ToolName]>)[tool];
  if (!v) return null;
  const parsed = v.props.safeParse(result);
  if (!parsed.success) return null;
  const View = VIEWS[v.view] as (p: { data: unknown; host: Host }) => ReactElement;
  return <View data={parsed.data} host={host} />;
}

/** The theme and Blueprint's base, once per document. */
export function injectStyles(doc: Document = document): void {
  if (doc.getElementById('flw-theme')) return;
  const t = generateTheme(blueprint, BLUEPRINT_DEFAULT_BRAND, { audience: 'staff' });
  if (!t.ok) throw new Error(`Blueprint theme: ${t.error.message}`);
  const style = doc.createElement('style');
  style.id = 'flw-theme';
  style.textContent = `${toCss(t.theme)}\n${baseCss}`;
  doc.head.prepend(style);
}

export interface MountedView {
  update(result: unknown): void;
  destroy(): void;
}

export function mountView(el: HTMLElement, opts: { tool: string; result: unknown; host: Host }): MountedView {
  injectStyles(el.ownerDocument);
  el.ownerDocument.documentElement.dataset.theme = opts.host.theme;
  if (opts.host.fontSans) el.style.setProperty('--font-sans', opts.host.fontSans);
  el.classList.add('flw', opts.host.surface === 'chat' ? 'flw-chat' : 'flw-office');
  const root = createRoot(el);
  const draw = (result: unknown) =>
    root.render(
      viewFor(opts.tool, result, opts.host) ?? (
        <p className="flw-empty" role="status">
          Flowology can’t show this answer here.{' '}
          <button type="button" className="flw-btn" onClick={() => opts.host.open({ kind: 'approvals' })}>
            Open Flowology
          </button>
        </p>
      ),
    );
  draw(opts.result);
  return { update: draw, destroy: () => root.unmount() };
}
